import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = 'https://api.civilcadpro.in/api';

function ActivationRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approveData, setApproveData] = useState({
    modules: 'All',
    durationValue: '1',
    durationUnit: 'year',
    customDays: ''
  });

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/activate/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openApproveModal = (req) => {
    setSelectedRequest(req);
    setApproveData({ modules: 'All', durationValue: '1', durationUnit: 'year', customDays: '' });
    setShowModal(true);
  };

  // Calculate total days from value + unit
  const getTotalDays = () => {
    if (approveData.durationUnit === 'custom') {
      return parseInt(approveData.customDays) || 365;
    }
    
    const value = parseInt(approveData.durationValue) || 1;
    
    switch (approveData.durationUnit) {
      case 'day': return value;
      case 'month': return value * 30;
      case 'year': return value * 365;
      default: return 365;
    }
  };

  const handleApprove = async () => {
    const days = getTotalDays();

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/activate/approve/${selectedRequest._id}`, {
        enabledModules: approveData.modules.split(',').map(m => m.trim()),
        expiryDays: days
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowModal(false);
      setSelectedRequest(null);
      fetchRequests();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Reject this request?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/activate/reject/${id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchRequests();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const getExpiryDate = () => {
    const days = getTotalDays();
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getDurationLabel = () => {
    if (approveData.durationUnit === 'custom') {
      return `${approveData.customDays || 0} Days (Custom)`;
    }
    
    const value = approveData.durationValue;
    const unit = approveData.durationUnit;
    const unitLabel = unit === 'day' ? 'Day(s)' : unit === 'month' ? 'Month(s)' : 'Year(s)';
    
    return `${value} ${unitLabel}`;
  };

  // Duration presets
  const presets = [
    { value: '7', unit: 'day', label: '7 Days' },
    { value: '15', unit: 'day', label: '15 Days' },
    { value: '1', unit: 'month', label: '1 Month' },
    { value: '3', unit: 'month', label: '3 Months' },
    { value: '6', unit: 'month', label: '6 Months' },
    { value: '1', unit: 'year', label: '1 Year' },
    { value: '2', unit: 'year', label: '2 Years' },
    { value: '0', unit: 'custom', label: 'Custom' },
  ];

  if (loading) return <div className="card">Loading...</div>;

  return (
    <div>
      <h2 style={{ marginBottom: 25, color: '#1a1a2e' }}>Activation Requests</h2>
      
      <div className="card">
        {requests.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#888', padding: 40 }}>No pending requests</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Customer</th>
                <th>Type</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Location</th>
                <th>Hardware ID</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map(req => (
                <tr key={req._id}>
                  <td>{new Date(req.createdAt).toLocaleDateString()}</td>
                  <td><strong>{req.customerName}</strong></td>
                  <td style={{ textTransform: 'capitalize' }}>{req.customerType}</td>
                  <td>{req.email}</td>
                  <td>{req.phone || '-'}</td>
                  <td>{req.location?.city || 'Unknown'}, {req.location?.country || 'Unknown'}</td>
                  <td style={{ fontSize: 11, fontFamily: 'monospace' }}>{req.hardwareId}</td>
                  <td><span className="status-badge status-pending">Pending</span></td>
                  <td>
                    <button className="btn btn-success btn-sm" onClick={() => openApproveModal(req)}>Approve</button>
                    {' '}
                    <button className="btn btn-danger btn-sm" onClick={() => handleReject(req._id)}>Reject</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Approve Modal */}
      {showModal && selectedRequest && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div style={{
            background: 'white', borderRadius: 12, padding: 30,
            width: 520, maxHeight: '90vh', overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h2 style={{ marginBottom: 5, color: '#1a1a2e' }}>Approve License</h2>
            <p style={{ color: '#888', marginBottom: 20, fontSize: 14 }}>
              For: <strong>{selectedRequest.customerName}</strong> ({selectedRequest.email})
            </p>

            {/* Quick Presets */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: 14, color: '#333' }}>
                Quick Duration
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
                {presets.map(preset => (
                  <button
                    key={preset.label}
                    onClick={() => setApproveData({ 
                      ...approveData, 
                      durationValue: preset.value, 
                      durationUnit: preset.unit 
                    })}
                    style={{
                      padding: '10px 6px',
                      border: `2px solid ${approveData.durationValue === preset.value && approveData.durationUnit === preset.unit ? '#ec700b' : '#ddd'}`,
                      borderRadius: 8,
                      background: approveData.durationValue === preset.value && approveData.durationUnit === preset.unit ? '#fff5f0' : 'white',
                      color: approveData.durationValue === preset.value && approveData.durationUnit === preset.unit ? '#ec700b' : '#333',
                      fontWeight: approveData.durationValue === preset.value && approveData.durationUnit === preset.unit ? 600 : 400,
                      cursor: 'pointer',
                      fontSize: 12,
                      transition: 'all 0.2s'
                    }}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Duration: Value + Unit */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: 14, color: '#333' }}>
                Custom Duration
              </label>
              <div style={{ display: 'flex', gap: 10 }}>
                <input
                  type="number"
                  value={approveData.durationValue}
                  onChange={(e) => setApproveData({ ...approveData, durationValue: e.target.value })}
                  placeholder="Value"
                  min="1"
                  style={{
                    width: '40%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: 8,
                    fontSize: 14
                  }}
                />
                <select
                  value={approveData.durationUnit}
                  onChange={(e) => setApproveData({ ...approveData, durationUnit: e.target.value })}
                  style={{
                    width: '60%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: 8,
                    fontSize: 14,
                    background: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="day">Days</option>
                  <option value="month">Months</option>
                  <option value="year">Years</option>
                </select>
              </div>
            </div>

            {/* Modules */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: 14, color: '#333' }}>
                Enabled Modules
              </label>
              <input
                type="text"
                value={approveData.modules}
                onChange={(e) => setApproveData({ ...approveData, modules: e.target.value })}
                placeholder="All (or comma separated: Earthwork, Contour, TIN)"
                style={{
                  width: '100%', padding: '12px', border: '1px solid #ddd',
                  borderRadius: 8, fontSize: 14
                }}
              />
            </div>

            {/* Summary */}
            <div style={{
              background: '#f8f9fa', padding: 15, borderRadius: 8,
              marginBottom: 20, fontSize: 14
            }}>
              <p style={{ margin: '0 0 5px 0', color: '#888' }}>Summary:</p>
              <p style={{ margin: 0, fontWeight: 600 }}>
                Duration: <span style={{ color: '#ec700b' }}>{getDurationLabel()}</span>
                {' → '}
                <span style={{ color: '#666' }}>{getTotalDays()} days</span>
              </p>
              <p style={{ margin: '5px 0 0 0', fontWeight: 600 }}>
                Expires: <span style={{ color: '#2ea043' }}>{getExpiryDate()}</span>
              </p>
              <p style={{ margin: '5px 0 0 0', fontWeight: 600 }}>
                Modules: <span style={{ color: '#2196F3' }}>{approveData.modules}</span>
              </p>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                className="btn"
                onClick={() => setShowModal(false)}
                style={{ background: '#eee', color: '#333' }}
              >
                Cancel
              </button>
              <button className="btn btn-success" onClick={handleApprove}>
                ✅ Approve License
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ActivationRequests;